import {
  parseSegments, serializeSegments, moveSegment, insertField, removeSegment,
} from './mergeSegments';
import type { MergeSegment } from './mergeSegments';

/**
 * Pure decision logic for `MergeTextEditor` — every drag/drop/type/remove
 * gesture reduces to "given the current stored string, produce the next
 * stored string". Kept separate from the component (and DOM-free, like
 * `mergeSegments.ts`) so it is unit-testable without a React/DOM test
 * harness, which this package does not have (see the test file for why).
 *
 * `MergeTextEditor` should stay a thin rendering shell that calls these and
 * nothing else — no drag-math or splicing inline in JSX handlers.
 */

/** Drag payload MIME for reordering an existing chip within one editor
 *  (dataTransfer carries the chip's segment index as a string). */
export const CHIP_REORDER_MIME = 'application/x-merge-chip-index';

/** Drag payload MIME for a NEW field dragged in from `MergeFieldPalette`
 *  (dataTransfer carries the catalog field name). */
export const FIELD_INSERT_MIME = 'application/x-merge-field-name';

export type DropEdge = 'before' | 'after';

/**
 * Character offset (the flattened cursor space `insertField` addresses,
 * where a field is width 1) immediately before `segments[index]` — or the
 * total length when `index === segments.length`, i.e. "the very end".
 */
export function offsetBeforeSegment(segments: readonly MergeSegment[], index: number): number {
  let pos = 0;
  const stop = Math.min(index, segments.length);
  for (let i = 0; i < stop; i++) {
    const seg = segments[i];
    pos += seg.kind === 'text' ? seg.text.length : 1;
  }
  return pos;
}

/**
 * Convert a "drop landed on segment `index`, on its `before`/`after` half"
 * gesture into the character offset `insertField` wants. Splitting a text
 * run mid-word from a drop position is not attempted — a drop lands at a
 * segment boundary, which is the granularity a pointer-driven drop can hit
 * reliably.
 */
export function boundaryOffset(
  segments: readonly MergeSegment[],
  index: number,
  edge: DropEdge,
): number {
  const before = offsetBeforeSegment(segments, index);
  if (edge === 'before') return before;
  const seg = segments[index];
  const width = seg ? (seg.kind === 'text' ? seg.text.length : 1) : 0;
  return before + width;
}

/** Insert a NEW field (from the palette) into `value` at character offset
 *  `atOffset`. Thin wrapper around `parseSegments`/`insertField`/
 *  `serializeSegments` so callers never touch segments directly. */
export function insertFieldIntoText(value: string, atOffset: number, fieldName: string): string {
  const segments = parseSegments(value);
  return serializeSegments(insertField(segments, atOffset, fieldName));
}

/**
 * Reorder: move the segment at `fromIndex` to just before `toIndexBeforeRemoval`
 * — both in the PRE-removal array-index space, i.e. `toIndexBeforeRemoval` is
 * where the pointer was dropped among the segments as they exist right now
 * (matching `boundaryOffset`'s `index`/`edge` when `edge==='after'` means
 * `index + 1`). `moveSegment` itself wants the destination in POST-removal
 * space, so a forward move is shifted back by one to account for the gap the
 * removal leaves — this function is the one place that adjustment happens,
 * so no call site has to reason about it.
 */
export function reorderSegment(
  value: string,
  fromIndex: number,
  toIndexBeforeRemoval: number,
): string {
  const segments = parseSegments(value);
  const to = toIndexBeforeRemoval > fromIndex ? toIndexBeforeRemoval - 1 : toIndexBeforeRemoval;
  return serializeSegments(moveSegment(segments, fromIndex, to));
}

/** Remove the chip (or text run) at `index`. */
export function removeFieldFromText(value: string, index: number): string {
  const segments = parseSegments(value);
  return serializeSegments(removeSegment(segments, index));
}

/** Replace the text of the text-run segment at `index` with `newText` — the
 *  effect of typing in that run's input. A no-op (returns `value` unchanged)
 *  if `index` isn't a text segment, which should never happen from the UI
 *  but keeps this safe to call defensively. */
export function replaceTextRun(value: string, index: number, newText: string): string {
  const segments = parseSegments(value);
  const seg = segments[index];
  if (!seg || seg.kind !== 'text') return value;
  const next = segments.slice();
  next[index] = { kind: 'text', text: newText };
  return serializeSegments(next);
}

/** Segments for an empty value render as a single empty text run so there is
 *  always something focusable to type or drop into — `parseSegments('')`
 *  returns `[]`, which is correct for storage but unusable as a render list. */
export function segmentsForEditing(value: string): MergeSegment[] {
  const segments = parseSegments(value);
  return segments.length > 0 ? segments : [{ kind: 'text', text: '' }];
}
